import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Copy, Key, Plus, Trash2 } from 'lucide-react'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/authStore'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'

interface ApiKey {
  id: string
  name: string
  key_prefix: string
  last_used_at: string | null
  created_at: string
  is_active: boolean
}

export function ApiKeysPage() {
  const { user } = useAuthStore()
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([])
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [newApiKey, setNewApiKey] = useState<string | null>(null)
  const [deletingKeys, setDeletingKeys] = useState<Set<string>>(new Set())

  const loadApiKeys = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('api_keys')
        .select('id, name, key_prefix, last_used_at, created_at, is_active')
        .eq('is_active', true)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error loading API keys:', error)
        toast.error('Failed to load API keys', {
          description: error.message,
        })
        return
      }

      setApiKeys(data || [])
    } catch (error) {
      console.error('Error loading API keys:', error)
      toast.error('Failed to load API keys', {
        description: 'An unexpected error occurred',
      })
    } finally {
      setLoading(false)
    }
  }

  const generateApiKey = async () => {
    if (!user?.id) {
      console.error('User not authenticated')
      toast.error('Authentication required', {
        description: 'Please log in to generate an API key',
      })
      return
    }

    setGenerating(true)
    try {
      const { data, error } = await supabase.rpc('generate_api_key_for_user', {
        user_uuid: user.id,
      })

      if (error) {
        console.error('Error generating API key:', error)
        toast.error('Failed to generate API key', {
          description: error.message,
        })
        return
      }

      setNewApiKey(data)
      toast.success('API key generated successfully', {
        description: "Make sure to copy it now - it won't be shown again",
      })
      await loadApiKeys()
    } catch (error) {
      console.error('Error generating API key:', error)
      toast.error('Failed to generate API key', {
        description: 'An unexpected error occurred',
      })
    } finally {
      setGenerating(false)
    }
  }

  const deleteApiKey = async (keyId: string) => {
    setDeletingKeys(prev => new Set(prev).add(keyId))
    try {
      const { error } = await supabase.from('api_keys').update({ is_active: false }).eq('id', keyId)

      if (error) {
        console.error('Error deleting API key:', error)
        toast.error('Failed to delete API key', {
          description: error.message,
        })
        return
      }

      toast.success('API key deleted successfully', {
        description: 'The API key has been deactivated and can no longer be used',
      })
      await loadApiKeys()
    } catch (error) {
      console.error('Error deleting API key:', error)
      toast.error('Failed to delete API key', {
        description: 'An unexpected error occurred',
      })
    } finally {
      setDeletingKeys(prev => {
        const newSet = new Set(prev)
        newSet.delete(keyId)
        return newSet
      })
    }
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success('Copied to clipboard')
    } catch (err) {
      console.error('Failed to copy to clipboard:', err)
      try {
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = text
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        toast.success('Copied to clipboard')
      } catch {
        toast.error('Failed to copy to clipboard')
      }
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  useEffect(() => {
    loadApiKeys()
  }, [])

  if (loading) {
    return (
      <div className='space-y-6'>
        <h1 className='text-2xl font-semibold'>API Keys</h1>
        <div className='flex items-center justify-center h-32'>
          <LoadingSpinner size='lg' />
        </div>
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-2xl font-semibold'>API Keys</h1>
          <p className='text-sm text-muted-foreground mt-1'>
            Manage access to the OpenArtifacts SDK
          </p>
        </div>
        <Button onClick={generateApiKey} disabled={generating}>
          {generating ? (
            <LoadingSpinner size='sm' className='mr-2' />
          ) : (
            <Plus className='w-4 h-4 mr-2' />
          )}
          Generate Key
        </Button>
      </div>

      {/* New API Key Alert */}
      {newApiKey && (
        <Card className='border-green-500/20 bg-green-500/5'>
          <CardHeader>
            <CardTitle className='text-green-700 dark:text-green-300'>
              API Key Generated Successfully
            </CardTitle>
            <CardDescription>Copy this key now - it won't be shown again</CardDescription>
          </CardHeader>
          <CardContent>
            <div className='flex items-center space-x-2'>
              <Input value={newApiKey} readOnly className='font-mono text-sm' />
              <Button size='sm' variant='outline' onClick={() => copyToClipboard(newApiKey)}>
                <Copy className='w-4 h-4' />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* API Keys List */}
      <Card>
        <CardHeader>
          <CardTitle>Your API Keys</CardTitle>
          <CardDescription>
            {apiKeys.length} active key{apiKeys.length !== 1 ? 's' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {apiKeys.length === 0 ? (
            <div className='text-center py-12'>
              <div className='mx-auto w-12 h-12 bg-muted rounded-lg flex items-center justify-center mb-4'>
                <Key className='w-6 h-6 text-muted-foreground' />
              </div>
              <h3 className='font-medium mb-2'>No API keys yet</h3>
              <p className='text-sm text-muted-foreground mb-6'>
                Generate your first API key to start using the OpenArtifacts SDK
              </p>
              <Button onClick={generateApiKey} disabled={generating} size='lg'>
                <Plus className='w-4 h-4 mr-2' />
                Generate Your First Key
              </Button>
            </div>
          ) : (
            <div className='space-y-4'>
              {apiKeys.map(apiKey => (
                <div
                  key={apiKey.id}
                  className='flex items-center justify-between p-4 border rounded-lg'
                >
                  <div className='space-y-1'>
                    <div className='flex items-center space-x-2'>
                      <span className='font-medium'>{apiKey.name}</span>
                      <Badge variant='outline'>{apiKey.key_prefix}...</Badge>
                    </div>
                    <div className='flex items-center space-x-4 text-sm text-muted-foreground'>
                      <span>Created {formatDate(apiKey.created_at)}</span>
                      {apiKey.last_used_at ? (
                        <span>Last used {formatDate(apiKey.last_used_at)}</span>
                      ) : (
                        <span>Never used</span>
                      )}
                    </div>
                  </div>
                  <div className='flex items-center space-x-2'>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size='sm'
                          variant='ghost'
                          disabled={deletingKeys.has(apiKey.id)}
                          className='text-destructive hover:text-destructive/80 hover:bg-destructive/10'
                        >
                          {deletingKeys.has(apiKey.id) ? (
                            <LoadingSpinner size='sm' />
                          ) : (
                            <Trash2 className='w-4 h-4' />
                          )}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete API Key</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete this API key? This action cannot be
                            undone and will immediately revoke access for any applications using
                            this key.
                            <br />
                            <br />
                            <strong>Key:</strong> {apiKey.key_prefix}...
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => deleteApiKey(apiKey.id)}
                            className='bg-destructive hover:bg-destructive/90'
                          >
                            Delete Key
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
